import type { Components, Theme } from '@mui/material';

import { colors } from '../tokens';

export const MuiTabs: Components<Theme>['MuiTabs'] = {
  variants: [
    {
      props: { variant: 'pill' },
      style: ({ theme }) => ({
        backgroundColor: colors.gray[100],
        borderRadius: theme.spacing(1.25),
        minHeight: 'unset',
        width: 'fit-content',
        ...theme.applyStyles('dark', {
          backgroundColor: colors.black[850],
        }),

        '& .MuiTabs-indicator': {
          display: 'none',
        },

        '& .MuiTabs-flexContainer': {
          gap: theme.spacing(0.1),
        },

        '& .MuiTab-root': {
          flexShrink: 0,
        },
      }),
    },
    {
      props: { variant: 'pill', fullWidth: true },
      style: {
        width: '100%',
        '& .MuiTab-root': {
          flex: '1 1 auto',
          whiteSpace: 'nowrap',
        },
      },
    },
    {
      props: { variant: 'compact' },
      style: ({ theme }) => ({
        border: `1px solid ${colors.gray[200]}`,
        borderRadius: theme.spacing(1),
        backgroundColor: colors.gray[50],
        minHeight: 'unset',
        width: 'fit-content',
        overflow: 'hidden',
        ...theme.applyStyles('dark', {
          border: `1px solid ${colors.black[700]}`,
          backgroundColor: colors.black[850],
        }),

        '& .MuiTabs-indicator': {
          display: 'none',
        },

        '& .MuiTabs-flexContainer': {
          gap: 0,
        },

        '& .MuiTab-root': {
          flexShrink: 0,
        },
      }),
    },
    {
      props: { variant: 'compact', fullWidth: true },
      style: {
        width: '100%',
        '& .MuiTab-root': {
          flex: '1 1 auto',
          whiteSpace: 'nowrap',
        },
      },
    },
  ],
};

export const MuiTab: Components<Theme>['MuiTab'] = {
  styleOverrides: {
    root: {
      textTransform: 'none',
      '&.Mui-selected': {
        color: colors.green[600],
      },
    },
  },
  variants: [
    {
      props: { variant: 'pill' },
      style: ({ theme }) => ({
        borderRadius: theme.spacing(0.75),
        minHeight: 'unset',
        padding: theme.spacing(1, 1.5),
        minWidth: 'unset',
        textTransform: 'none',
        fontSize: theme.typography.pxToRem(14),
        color: colors.gray[500],
        ...theme.applyStyles('dark', {
          color: colors.black[200],
        }),
        transition: 'background 0.15s, color 0.15s',
        margin: theme.spacing(0.5),

        '&:hover': {
          backgroundColor: colors.common.white,
          color: colors.black[700],
          ...theme.applyStyles('dark', {
            backgroundColor: colors.grayTrue[850],
            color: colors.black[100],
          }),
        },

        '&.Mui-selected': {
          backgroundColor: colors.common.white,
          color: colors.black[700],
          fontWeight: 500,
          boxShadow:
            '0px 1px 2px -1px rgba(10, 13, 18, 0.1), 0px 1px 3px 0px rgba(10, 13, 18, 0.1)',
          ...theme.applyStyles('dark', {
            backgroundColor: colors.grayTrue[850],
            color: colors.black[100],
          }),
        },
      }),
    },
    {
      props: { variant: 'compact' },
      style: ({ theme }) => ({
        borderRadius: 0,
        minHeight: 'unset',
        padding: theme.spacing(1.5, 2.5),
        minWidth: 'unset',
        textTransform: 'none',
        fontSize: theme.typography.pxToRem(14),
        fontWeight: 600,
        color: colors.gray[500],
        borderRight: `1px solid ${colors.gray[200]}`,
        transition: 'background 0.15s, color 0.15s',

        '&:last-child': {
          borderRight: 'none',
        },

        ...theme.applyStyles('dark', {
          color: colors.black[200],
          borderRight: `1px solid ${colors.black[700]}`,
          '&:last-child': { borderRight: 'none' },
        }),

        '&:hover': {
          backgroundColor: colors.gray[100],
          color: colors.black[700],
          ...theme.applyStyles('dark', {
            backgroundColor: colors.grayTrue[850],
            color: colors.black[100],
          }),
        },

        '&.Mui-selected': {
          backgroundColor: colors.common.white,
          color: colors.black[700],
          ...theme.applyStyles('dark', {
            backgroundColor: colors.grayTrue[850],
            color: colors.black[100],
          }),
        },
      }),
    },
  ],
};
